// src/utils/authFetch.js

import {
  clearStoredAuthData,
  getStoredAuthTokens,
  persistAuthTokens,
  isTokenAuthEnabled,
} from "./auth";
import { appendCsrfHeader } from "./csrf";
import { extractReferralSourceFromPath } from "./referralLinks";
const UTM_STORAGE_PREFIX = "pm_utm_";
const UTM_LANDING_KEY = "pm_utm_landing";

const UTM_SOURCE_PARAM = "utm_source";
const UTM_SOURCE_PATHS = [
  /\/api\/musas\/?$/,
  /\/api\/musas\/[^/]+\/?$/,
  /\/api\/profile\/?$/,
  /\/api\/clientes\/me\/favoritos\/?$/,
  /\/api\/me\/favoritos\/?$/,
  /\/api\/me\/recently-viewed\/?$/,
];

function resolveApiUrl(path) {
  const raw = String(path || "");

  if (/^https?:\/\//i.test(raw)) return raw;

  let cleaned = raw.replace(/^\/+/, "");
  if (!cleaned.startsWith("api/")) cleaned = `api/${cleaned}`;

  const base = (process.env.REACT_APP_API_URL || "").replace(/\/+$/, "");
  const finalPath = `/${cleaned}`;

  return base ? `${base}${finalPath}` : finalPath;
}

function shouldAttachUtmSource(pathname = "") {
  return UTM_SOURCE_PATHS.some((pattern) => pattern.test(pathname));
}

function safeGet(storage, key) {
  try {
    return storage?.getItem(key) ?? null;
  } catch (_) {
    return null;
  }
}

function tryParseJson(value) {
  if (!value) return null;
  try {
    return JSON.parse(value);
  } catch (_) {
    return null;
  }
}

function readCookie(name) {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(
    new RegExp(`(?:^|; )${name.replace(/[.*+?^${}()|[\\]\\\\]/g, "\\\\$&")}=([^;]*)`)
  );
  return match ? decodeURIComponent(match[1]) : null;
}

function readLandingData() {
  if (typeof window === "undefined") return null;
  const rawLocal = safeGet(window.localStorage, UTM_LANDING_KEY);
  if (rawLocal) {
    const parsed = tryParseJson(rawLocal);
    if (parsed && typeof parsed === "object") {
      if (parsed.value && typeof parsed.value === "object") return parsed.value;
      if (parsed.utm_source !== undefined || parsed.first_seen_at) return parsed;
    }
  }

  const rawCookie = readCookie(UTM_LANDING_KEY);
  if (!rawCookie) return null;
  const parsedCookie = tryParseJson(rawCookie);
  if (parsedCookie && typeof parsedCookie === "object") {
    if (parsedCookie.value && typeof parsedCookie.value === "object") {
      return parsedCookie.value;
    }
    return parsedCookie;
  }

  return null;
}

function getUtmValue(param) {
  if (typeof window === "undefined") return "";
  const landing = readLandingData();
  if (landing && landing[param] != null) return String(landing[param]);

  if (param === UTM_SOURCE_PARAM) {
    const sourceFromPath = extractReferralSourceFromPath(window.location.pathname);
    if (sourceFromPath) return sourceFromPath;
  }

  const legacyKey = `${UTM_STORAGE_PREFIX}${param}`;
  const legacy =
    safeGet(window.localStorage, legacyKey) ??
    safeGet(window.sessionStorage, legacyKey);
  return legacy == null ? "" : String(legacy);
}

function appendUtmSource(apiUrl) {
  const utmSource = String(getUtmValue(UTM_SOURCE_PARAM) || "").trim();
  if (!utmSource) return apiUrl;

  let parsed;
  try {
    const base =
      typeof window !== "undefined" && window.location?.origin
        ? window.location.origin
        : "http://localhost";
    parsed = new URL(apiUrl, base);
  } catch (_) {
    return apiUrl;
  }

  if (!shouldAttachUtmSource(parsed.pathname)) return apiUrl;
  if (parsed.searchParams.has(UTM_SOURCE_PARAM)) return apiUrl;

  parsed.searchParams.set(UTM_SOURCE_PARAM, utmSource);

  if (/^https?:\/\//i.test(apiUrl)) return parsed.toString();
  return `${parsed.pathname}${parsed.search}${parsed.hash}`;
}

function isFormData(body) {
  return typeof FormData !== "undefined" && body instanceof FormData;
}

function buildRequestInit(options = {}) {
  const init = { ...options };
  const method = String(init.method || "GET").toUpperCase();
  init.method = method;

  const headers = { ...(options.headers || {}) };
  const body = options.body;

  if (body && !isFormData(body) && !headers["Content-Type"]) {
    headers["Content-Type"] = "application/json";
  }
  if (!headers["Accept"]) headers["Accept"] = "application/json";

  appendCsrfHeader(headers, method);

  init.headers = headers;
  if (!init.credentials) init.credentials = "include";

  return init;
}

function buildRefreshInit(refreshToken) {
  const payload = refreshToken ? { refresh: refreshToken } : {};
  return buildRequestInit({
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });
}

export async function authFetch(
  url,
  options = {},
  navigate,
  { requireAuth = true, autoLogout = true } = {}
) {
  const apiUrl = appendUtmSource(resolveApiUrl(url));
  const tokenAuthEnabled = isTokenAuthEnabled();
  const { accessToken: storedAccessToken, refreshToken: storedRefreshToken } = getStoredAuthTokens();

  const makeRequest = (tokenOverride) => {
    const init = buildRequestInit(options);
    const headers = { ...(init.headers || {}) };
    const hasCustomAuthHeader = Boolean(headers.Authorization);

    if (!tokenAuthEnabled) {
      if (headers.Authorization) delete headers.Authorization;
    } else if (!hasCustomAuthHeader) {
      if (tokenOverride === null) delete headers.Authorization;
      else if (tokenOverride) headers.Authorization = `Bearer ${tokenOverride}`;
      else if (storedAccessToken) headers.Authorization = `Bearer ${storedAccessToken}`;
    }

    return fetch(apiUrl, { ...init, headers });
  };

  const refreshTokens = async () => {
    const refreshUrl = resolveApiUrl("token/refresh/");
    const refreshRes = await fetch(refreshUrl, buildRefreshInit(storedRefreshToken));

    let refreshData = {};
    try {
      refreshData = await refreshRes.clone().json();
    } catch (_) {
      refreshData = {};
    }
    const detail = refreshData?.detail || refreshData?.code || "";

    if (refreshRes.ok) {
      persistAuthTokens(refreshData);
      const nextAccess =
        refreshData?.access ||
        refreshData?.access_token ||
        refreshData?.accessToken ||
        null;
      return { ok: true, accessToken: nextAccess, detail };
    }

    return { ok: false, detail, status: refreshRes.status };
  };

  let res = await makeRequest();

  if (!requireAuth && (res.status === 401 || res.status === 403) && storedAccessToken) {
    res = await makeRequest(null);
  }

  if (requireAuth && res.status === 401) {
    const refreshResult = await refreshTokens();

    if (refreshResult.ok) {
      res = await makeRequest(refreshResult.accessToken || null);
    } else {
      const missingRefreshCookie =
        refreshResult.status === 401 &&
        refreshResult.detail === "refresh_cookie_missing";

      if (!missingRefreshCookie) {
        clearStoredAuthData();
        if (autoLogout) {
          if (navigate) navigate("/login", { replace: true });
          else window.location.href = "/login";
        }
        throw new Error("Sessão expirada");
      }
      return res;
    }
  }

  if (requireAuth && (res.status === 401 || res.status === 403)) {
    let detail = "";
    try {
      const data = await res.clone().json();
      detail = data?.detail || data?.code || "";
    } catch (_) {}
    const normalizedDetail = String(detail || "").trim();
    const normalizedDetailLower = normalizedDetail.toLowerCase();
    const authNotProvided =
      normalizedDetailLower.includes("authentication credentials were not provided") ||
      normalizedDetailLower.includes("authentication was not provided");
    const tokenIssue =
      authNotProvided ||
      ["token_not_valid", "credentials_not_provided", "not authenticated"].includes(
        normalizedDetailLower
      );
    if (!tokenIssue) return res;

    const nonBlockingDetails = new Set(["credentials_not_provided", "not authenticated"]);
    if (!authNotProvided && nonBlockingDetails.has(normalizedDetailLower)) {
      return res;
    }

    clearStoredAuthData();
    if (autoLogout) {
      if (navigate) navigate("/login", { replace: true, state: { msg: "Sessão expirada." } });
      else window.location.href = "/login";
    }
    throw new Error("Sessão expirada");
  }

  return res;
}

export default authFetch;
